import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Link,
  Stack,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from "@mui/material";
import { Icon } from "@iconify/react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useSnackbar } from "../../components/snackbar";
import VenueHeader from "../Venue/VenueHeader";
import FooterLogin from "src/layouts/compact/FooterLogin";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export default function SupportPage() {
  const schema = yup.object().shape({
    name: yup
      .string()
      .required("Name is required")
      .max(50, "Max 50 characters"),
    email: yup
      .string()
      .required("Email is required")
      .email("Enter a valid email"),
    issue: yup
      .string()
      .required("Please describe your issue")
      .min(10, "Minimum 10 characters"),
    orderId: yup
      .number()
      .typeError("Order ID must be a number")
      .nullable()
      .transform((value, originalValue) =>
        String(originalValue).trim() === "" ? null : value
      ),
  });

  const faqs = [
    { question: "How do I reset my password?", answer: "You can reset..." },
    {
      question: "How can I change my saved payment method?",
      answer: "Go to Settings...",
    },
    {
      question: "Why am I not receiving order confirmation emails?",
      answer: "Please check your spam...",
    },
    {
      question: "Can I cancel or modify an order after placing it?",
      answer: "Orders can be modified...",
    },
    {
      question: "How do I report an issue with a restaurant or venue?",
      answer: "Go to the venue's page...",
    },
  ];

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { name: "", email: "", issue: "", orderId: "" },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const res = await axios.post(`${backendUrl}/customerSupport`, {
        ...data,
        orderId: data.orderId ? Number(data.orderId) : null,
      });

      const message =
        res.data.message || "Support request submitted successfully.";
      enqueueSnackbar(message, { variant: "success" });

      setSuccessMessage(
        "Thank you! We’ve received your request and our support team will contact you shortly."
      );
      reset();
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || "Something went wrong.",
        { variant: "error" }
      );
      setError("afterSubmit", {
        ...error,
        message: error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <VenueHeader />
      <Container maxWidth="md" sx={{ py: 4, mt: 12, mb: 6 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", mb: 2, color: "#F59B34" }}
        >
          Egora App Support
        </Typography>
        <Typography sx={{ mb: 3 }}>
          Welcome to the Egora Support Page! If you’re facing an issue...
        </Typography>

        {/* FAQ */}
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", mb: 2, color: "#2c3e50" }}
        >
          Frequently Asked Questions (FAQ)
        </Typography>
        {faqs.map((faq, index) => (
          <Accordion key={index} sx={{ mb: 1 }}>
            <AccordionSummary
              expandIcon={
                <Icon icon="mdi:chevron-down" width={24} height={24} />
              }
            >
              <Typography sx={{ fontWeight: "bold" }}>
                {index + 1}. {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>{faq.answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Form */}
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", mt: 4, mb: 2, color: "#2c3e50" }}
        >
          📝 Still Need Help?
        </Typography>
        <Paper sx={{ p: 3, mb: 4 }} elevation={2}>
          {successMessage && (
            <Typography
              sx={{
                mb: 2,
                fontWeight: "bold",
                color: "#F59B34",
              }}
            >
              {successMessage}
            </Typography>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <TextField
                label="Name"
                {...register("name")}
                error={!!errors.name}
                helperText={errors.name?.message}
              />
              <TextField
                label="Email"
                type="email"
                {...register("email")}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
              <TextField
                label="Issue / Question"
                multiline
                rows={4}
                {...register("issue")}
                error={!!errors.issue}
                helperText={errors.issue?.message}
              />
              <TextField
                label="Order ID (if applicable)"
                {...register("orderId")}
                error={!!errors.orderId}
                helperText={errors.orderId?.message}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ bgcolor: "#F59B34", fontWeight: "bold" }}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Submit"
                )}
              </Button>
            </Stack>
          </form>
        </Paper>
      </Container>
      <FooterLogin />
    </>
  );
}
